import React from "react";
import { Link } from "react-router-dom";

const NODE_SIZE = 20;
const STROKE = 2;
const R = (NODE_SIZE - STROKE) / 2 - 0.5;
const C = 2 * Math.PI * R;

const AUDIT_STEPS = [
  {
    id: "emails",
    label: "Fetching emails",
    readyKey: "emails_ready",
    foundKey: "emails_found",
    processedKey: "emails_processed",
    eligibleKey: "emails_expected",
  },
  {
    id: "metadata",
    label: "Scanning content",
    readyKey: "metadata_ready",
    foundKey: "metadata_found",
    processedKey: "metadata_processed",
    eligibleKey: "metadata_eligible",
  },
  {
    id: "human_sender",
    label: "Looking up senders",
    readyKey: "human_sender_ready",
    foundKey: "human_sender_found",
    processedKey: "human_sender_processed",
    eligibleKey: "human_sender_eligible",
  },
  {
    id: "company",
    label: "Researching companies",
    readyKey: "company_ready",
    foundKey: "company_found",
    processedKey: "company_processed",
    eligibleKey: "company_eligible",
  },
  {
    id: "cold_pitch",
    label: "Scoring inbound pitches",
    readyKey: "cold_pitch_ready",
    foundKey: "cold_pitch_found",
    processedKey: "cold_pitch_processed",
    eligibleKey: "cold_pitch_eligible",
  },
  {
    id: "lead",
    label: "Assessing High-fit leads",
    readyKey: "lead_ready",
    foundKey: "lead_found",
    processedKey: "lead_processed",
    eligibleKey: "lead_eligible",
  },
];

const STATUS_LABELS = {
  pending: "Starting",
  running: "Running",
  partially_completed: "Wrapping up",
  completed: "Complete",
  failed: "Failed",
  timed_out: "Timed out",
};

const FAILED_STATUS = "failed";

function pct(processed, eligible) {
  if (processed == null || eligible == null || eligible === 0) return 0;
  return Math.min(100, Math.max(0, (processed / eligible) * 100));
}

function isComplete(step) {
  return Boolean(step.ready) && step.eligible != null && step.processed === step.eligible;
}

function formatCount(n) {
  if (n == null) return "—";
  return n.toLocaleString();
}

function ExpectedSuffix({ eligible }) {
  return (
    <div className="flex items-baseline justify-start text-left">
      <span className="font-mono text-[9px] leading-none text-theme-muted opacity-50 mx-1"> / </span>
      <span className="font-mono text-[9px] tabular-nums leading-none text-theme-muted opacity-50">
        {formatCount(eligible)}
      </span>
    </div>
  );
}

function formatDuration(seconds) {
  if (seconds == null) return null;
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

function buildSteps(data) {
  return AUDIT_STEPS.map((config) => ({
    id: config.id,
    label: config.label,
    ready: data[config.readyKey],
    found: data[config.foundKey],
    processed: data[config.processedKey],
    eligible: data[config.eligibleKey],
  }));
}

function StepCompleteIcon() {
  return (
    <span className="flex size-5 shrink-0 items-center justify-center" aria-hidden>
      <span className="material-symbols-outlined text-[11px] leading-none text-theme-accent">check</span>
    </span>
  );
}

function StepFailedIcon() {
  return (
    <span className="flex size-5 shrink-0 items-center justify-center" aria-hidden>
      <span className="material-symbols-outlined text-[14px] leading-none text-red-600">close</span>
    </span>
  );
}

function StepRing({ progress }) {
  const offset = C - (progress / 100) * C;

  return (
    <span className="relative size-5 shrink-0">
      <svg
        className="absolute inset-0 size-full -rotate-90"
        viewBox={`0 0 ${NODE_SIZE} ${NODE_SIZE}`}
        aria-hidden
      >
        <circle
          cx={NODE_SIZE / 2}
          cy={NODE_SIZE / 2}
          r={R}
          fill="none"
          className="stroke-theme-border opacity-25"
          strokeWidth={STROKE}
        />
        <circle
          cx={NODE_SIZE / 2}
          cy={NODE_SIZE / 2}
          r={R}
          fill="none"
          className="stroke-theme-dim opacity-25"
          strokeWidth={STROKE}
          strokeDasharray={C}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 500ms ease-out" }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center">
        <span
          className="inline-block size-2 rounded-full border-[1.5px] border-theme-dim/40 border-t-theme-dim animate-spin"
          aria-hidden
        />
      </span>
    </span>
  );
}

function StepNode({ step, auditFailed }) {
  if (isComplete(step)) return <StepCompleteIcon />;
  if (auditFailed) return <StepFailedIcon />;
  return <StepRing progress={pct(step.processed, step.eligible)} />;
}

function PipelineStep({ step, isLast, auditFailed }) {
  const complete = isComplete(step);
  const dull = !step.ready;

  return (
    <div
      className={`relative grid grid-cols-[1.25rem_minmax(0,1fr)_3rem_3.5rem] items-center gap-x-2.5 ${
        isLast ? "" : "pb-2"
      } ${dull ? "opacity-50" : ""}`}
    >
      {!isLast ? (
        <span
          className={`pointer-events-none absolute left-[0.625rem] top-5 bottom-0 w-px -translate-x-1/2 ${
            complete ? "bg-theme-accent/50" : "bg-theme-border"
          }`}
          aria-hidden
        />
      ) : null}

      <div className="relative flex items-center justify-center">
        <StepNode step={step} auditFailed={auditFailed} />
      </div>

      <p className="min-w-0 font-mono text-[11px] leading-none text-theme-text">{step.label}</p>

      <p className="text-right font-mono text-[11px] tabular-nums leading-none text-theme-text">
        {formatCount(step.found)}
      </p>

      <div className="justify-self-start">
        <ExpectedSuffix eligible={step.eligible} />
      </div>
    </div>
  );
}

export function InboxAuditPipeline({ data }) {
  const p = data || {};
  const steps = buildSteps(p);
  const auditFailed = p.status === FAILED_STATUS;
  const statusLabel = STATUS_LABELS[p.status] || p.status?.replace(/_/g, " ") || "…";
  const duration = formatDuration(p.duration);

  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-theme-border bg-gradient-to-b from-theme-panel/80 to-theme-panel/30">
      {auditFailed ? (
        <div className="border-b border-theme-border/60 px-3 py-2.5">
          <p className="text-sm text-theme-text">
            {p.emails_found > 0 ? (
              <>Import failed. Please try again.</>
            ) : (
              <>
                Failed to fetch messages, try{" "}
                <Link to="/settings/mailboxes/" className="text-theme-primary underline hover:opacity-80">
                  unlinking/relinking your mailbox
                </Link>
                .
              </>
            )}
          </p>
        </div>
      ) : null}
      <div className="border-b border-theme-border/60 px-3 py-2">
        <p className="font-mono text-[11px] text-theme-text">
          {statusLabel}
          {duration ? <span className="text-theme-muted"> · {duration}</span> : null}
        </p>
      </div>

      <div className="px-3 py-2.5">
        {steps.map((step, i) => (
          <PipelineStep key={step.id} step={step} isLast={i === steps.length - 1} auditFailed={auditFailed} />
        ))}
      </div>
    </div>
  );
}
